import { courseCatalogSlots } from '@/features/catalog/course-catalog/paths'
import { courseOverviewSlot } from '@/features/catalog/courses/paths'
import { enrollmentClassPanelSlot } from '@/features/entitlements/enrollments/paths'
import { studentDashboardSlots } from '@/features/entitlements/my-training/paths'
// The slot name's own module, not the organizations folder's index, which would pull its chooser into the main bundle.
import { organizationDetailsSlot } from '@/features/site-management/organizations/organization'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { detailsSlots } from '@/shared/details'
import type { Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'
import { CalendarDays, DoorOpen, GraduationCap, Package, UserCheck, Video } from '@/shared/ui/icons'

import { attendanceChartRoute, signInSheetRoute, studentCheckInPath, studentCheckInRoute } from './attendance/attendance'
import { checklistRoute, materialsResponsibilitiesPath, materialsResponsibilitiesRoute } from './checklist/materials'
import { ClassesProvider } from './ClassesProvider'
import { instructorMonthRoute, instructorSchedulePath } from './calendars/instructor'
import {
  instructorCalendarPath,
  instructorCalendarRoute,
  organizationCalendarPath,
  organizationCalendarRoute,
  roomCalendarPath,
  roomCalendarRoute,
} from './calendars/paths'
import { classDetailsSidebar, classDetailsTabs } from './class-details/registry'
import { classDetailsSlot, classRoute, createClassPath, editClassRoute, organizationScopes, readClassesPermission, writeClassesPermission } from './class-form/class'
import { classFormSections } from './class-form/sections'
import { classroomRoute, createClassroomPath, editClassroomRoute } from './classrooms/paths'
import {
  classCustomFieldRoute,
  classCustomFieldsPath,
  createClassCustomFieldPath,
  editClassCustomFieldRoute,
  readClassCustomFieldsPermission,
  writeClassCustomFieldsPermission,
} from './custom-fields/paths'
import { instructorDashboardNav, instructorDashboardPath } from './dashboard/dashboard'
import { addFileRoute, editFileRoute, manageFilesRoute } from './files/class-file'
import { findAttendancePath, findMeetingJoinsPath } from './find/attendance-search'
import { classSchedulePath, findClassesPath, findSessionsPath } from './find/listing'
import { classBrowserNav, classBrowserPath, learnerClassRoute, publicScheduleRoute } from './learner/learner'
import { myDataApi } from './my-data/paths'
import { classRosterRoute, enrollmentResultRoute, readClassRosterPermission } from './roster/roster'
import { classSlots } from './slots'
import {
  createWebExConnectionPath,
  editWebExConnectionRoute,
  readWebExConnectionsPermission,
  webExConnectionRoute,
  webExConnectionsPath,
} from './webex/paths'

/**
 * Classes: classes and sessions, instructors, classrooms, rosters and attendance. Its screens
 * register here; its own class form sections and Class Details tabs and links come from its two
 * registries, and other areas add theirs through `classSlots`.
 */
export const feature: Feature = {
  id: 'classes',
  // Shows Browse Classes only while the viewer's organization has its class browser switch on, and links a class only for who may open it.
  provider: ClassesProvider,
  // Create, Details and Edit Classroom (CLS-02).
  routes: [
    // Create, Details and Edit Class (CLS-04).
    {
      path: createClassPath,
      titleId: 'classes.classForm.create.title',
      page: () => import('./class-form/ClassFormPages').then((module) => module.CreateClassPage),
    },
    {
      path: classRoute,
      titleId: 'classes.classDetails.title',
      page: () => import('./class-details/ClassDetailsPage').then((module) => module.ClassDetailsPage),
    },
    {
      path: editClassRoute,
      titleId: 'classes.classForm.edit.title',
      page: () => import('./class-form/ClassFormPages').then((module) => module.EditClassPage),
    },
    // Find, Create, Details and Edit Class Custom Field (CLS-19).
    {
      path: classCustomFieldsPath,
      titleId: 'classes.customFields.find.title',
      page: () => import('./custom-fields/FindClassCustomFieldsPage').then((module) => module.FindClassCustomFieldsPage),
    },
    {
      path: createClassCustomFieldPath,
      titleId: 'classes.customFields.create.title',
      page: () => import('./custom-fields/ClassCustomFieldFormPages').then((module) => module.CreateClassCustomFieldPage),
    },
    {
      path: classCustomFieldRoute,
      titleId: 'classes.customFields.details.title',
      page: () => import('./custom-fields/ClassCustomFieldDetailsPage').then((module) => module.ClassCustomFieldDetailsPage),
    },
    {
      path: editClassCustomFieldRoute,
      titleId: 'classes.customFields.edit.title',
      page: () => import('./custom-fields/ClassCustomFieldFormPages').then((module) => module.EditClassCustomFieldPage),
    },
    // Class Roster, from Class Details' side menu (CLS-10).
    {
      path: classRosterRoute,
      titleId: 'classes.roster.title',
      page: () => import('./roster/ClassRosterPage').then((module) => module.ClassRosterPage),
    },
    // The attendance chart and sign-in sheet, from Class Details' side menu, and an organization's daily check-in page (CLS-12).
    {
      path: attendanceChartRoute,
      titleId: 'classes.attendance.chart.title',
      page: () => import('./attendance/AttendanceChartPage').then((module) => module.AttendanceChartPage),
    },
    {
      path: signInSheetRoute,
      titleId: 'classes.attendance.signInSheet.title',
      page: () => import('./attendance/SignInSheetPage').then((module) => module.SignInSheetPage),
    },
    {
      path: studentCheckInRoute,
      titleId: 'classes.attendance.checkIn.title',
      page: () => import('./attendance/StudentCheckInPage').then((module) => module.StudentCheckInPage),
    },
    // The pre-class checklist, from Class Details' side menu, and an organization's materials responsibilities (CLS-13).
    {
      path: checklistRoute,
      titleId: 'classes.checklist.title',
      page: () => import('./checklist/ChecklistPage').then((module) => module.ChecklistPage),
    },
    {
      path: materialsResponsibilitiesRoute,
      titleId: 'classes.checklist.responsibilities.title',
      page: () => import('./checklist/MaterialsResponsibilitiesPage').then((module) => module.MaterialsResponsibilitiesPage),
    },
    // Manage Files, from Class Details' side menu, and Add and Edit File (CLS-17).
    {
      path: manageFilesRoute,
      titleId: 'classes.files.manage.title',
      page: () => import('./files/ManageFilesPage').then((module) => module.ManageFilesPage),
    },
    {
      path: addFileRoute,
      titleId: 'classes.files.add.title',
      page: () => import('./files/ClassFileFormPages').then((module) => module.AddClassFilePage),
    },
    {
      path: editFileRoute,
      titleId: 'classes.files.edit.title',
      page: () => import('./files/ClassFileFormPages').then((module) => module.EditClassFilePage),
    },
    // One enrollment's completion status, grade and hours, from the roster (CLS-11).
    {
      path: enrollmentResultRoute,
      titleId: 'classes.enrollmentPanel.title',
      page: () => import('./enrollment-panel/EnrollmentResultPage').then((module) => module.EnrollmentResultPage),
    },
    // Find Classes, Find Sessions, the class schedule and the learner's class pages (CLS-05).
    {
      path: findClassesPath,
      titleId: 'classes.find.classes.title',
      page: () => Promise.all([import('./find/FindClassesPage'), import('./find/admin-only')]).then(([module, gate]) => gate.adminOnly(module.FindClassesPage, module.findClassesHeading)),
    },
    {
      path: findSessionsPath,
      titleId: 'classes.find.sessions.title',
      page: () => Promise.all([import('./find/FindSessionsPage'), import('./find/admin-only')]).then(([module, gate]) => gate.adminOnly(module.FindSessionsPage, module.findSessionsHeading)),
    },
    {
      path: classSchedulePath,
      titleId: 'classes.find.schedule.title',
      page: () => Promise.all([import('./find/FindClassesPage'), import('./find/admin-only')]).then(([module, gate]) => gate.adminOnly(module.ClassSchedulePage, module.classScheduleHeading)),
    },
    // Find Class Attendance and Find Meeting Joins, for whoever reads rosters: administrators, and instructors for their own classes (CLS-27).
    {
      path: findAttendancePath,
      titleId: 'classes.find.attendance.title',
      page: () => Promise.all([import('./find/FindAttendancePage'), import('./find/admin-only')]).then(([module, gate]) => gate.holdersOnly(module.FindAttendancePage, module.findAttendanceHeading, readClassRosterPermission)),
    },
    {
      path: findMeetingJoinsPath,
      titleId: 'classes.find.meetingJoins.title',
      page: () => Promise.all([import('./find/FindMeetingJoinsPage'), import('./find/admin-only')]).then(([module, gate]) => gate.holdersOnly(module.FindMeetingJoinsPage, module.findMeetingJoinsHeading, readClassRosterPermission)),
    },
    {
      // Public: signed out, it opens when the site allows anonymous class browsing.
      path: classBrowserPath,
      titleId: 'classes.learner.browser.title',
      page: () => import('./learner/ClassBrowserPage').then((module) => module.ClassBrowserPage),
      public: true,
    },
    {
      // Public: signed out, it opens when the class lets anyone see its page.
      path: learnerClassRoute,
      titleId: 'classes.learner.page.eyebrow',
      page: () => import('./learner/LearnerClassPage').then((module) => module.LearnerClassPage),
      public: true,
    },
    {
      // Public: signed out, it opens when the site allows anonymous access to public schedules.
      path: publicScheduleRoute,
      titleId: 'classes.learner.publicSchedule.title',
      page: () => import('./public/PublicSchedulePage').then((module) => module.PublicSchedulePage),
      public: true,
    },
    {
      path: createClassroomPath,
      titleId: 'classes.classrooms.create.title',
      page: () => import('./classrooms/ClassroomFormPages').then((module) => module.CreateClassroomPage),
    },
    {
      path: classroomRoute,
      titleId: 'classes.classrooms.details.title',
      page: () => import('./classrooms/ClassroomDetailsPage').then((module) => module.ClassroomDetailsPage),
    },
    {
      path: editClassroomRoute,
      titleId: 'classes.classrooms.edit.title',
      page: () => import('./classrooms/ClassroomFormPages').then((module) => module.EditClassroomPage),
    },
    // The instructor dashboard: today's sessions, the week and the month (CLS-26).
    {
      path: instructorDashboardPath,
      titleId: 'classes.dashboard.title',
      page: () => import('./dashboard/InstructorDashboardPage').then((module) => module.InstructorDashboardPage),
    },
    // Find, Create, Details and Edit WebEx Connection (CLS-03).
    {
      path: webExConnectionsPath,
      titleId: 'classes.webex.find.title',
      page: () => import('./webex/FindWebExConnectionsPage').then((module) => module.FindWebExConnectionsPage),
    },
    {
      path: createWebExConnectionPath,
      titleId: 'classes.webex.create.title',
      page: () => import('./webex/WebExConnectionFormPages').then((module) => module.CreateWebExConnectionPage),
    },
    {
      path: webExConnectionRoute,
      titleId: 'classes.webex.details.title',
      page: () => import('./webex/WebExConnectionDetailsPage').then((module) => module.WebExConnectionDetailsPage),
    },
    {
      path: editWebExConnectionRoute,
      titleId: 'classes.webex.edit.title',
      page: () => import('./webex/WebExConnectionFormPages').then((module) => module.EditWebExConnectionPage),
    },
    // An organization's calendar, room calendar and instructor calendar (CLS-24).
    {
      path: organizationCalendarRoute,
      titleId: 'classes.calendars.organization.title',
      page: () => import('./calendars/OrganizationCalendarPage').then((module) => module.OrganizationCalendarPage),
    },
    {
      path: roomCalendarRoute,
      titleId: 'classes.calendars.room.title',
      page: () => import('./calendars/RoomCalendarPage').then((module) => module.RoomCalendarPage),
    },
    {
      path: instructorCalendarRoute,
      titleId: 'classes.calendars.instructor.title',
      page: () => import('./calendars/InstructorCalendarPage').then((module) => module.InstructorCalendarPage),
    },
    // An instructor's own calendar and the site-wide Instructor Schedule (CLS-25).
    {
      path: instructorMonthRoute,
      titleId: 'classes.calendars.instructorMonth.title',
      page: () => import('./calendars/InstructorMonthPage').then((module) => module.InstructorMonthPage),
    },
    {
      path: instructorSchedulePath,
      titleId: 'classes.calendars.schedule.title',
      page: () => Promise.all([import('./calendars/InstructorSchedulePage'), import('./find/admin-only')]).then(([module, gate]) => gate.adminOnly(module.InstructorSchedulePage, module.instructorScheduleHeading)),
    },
  ],
  nav: [
    classBrowserNav,
    instructorDashboardNav,
  ],
  adminHome: {
    // Legacy's Virtual Meetings category, with WebEx its one kept provider (CLS-03). Creating needs
    // two permissions, which a link cannot name, so Find offers Create to whoever holds both.
    categories: [{ id: 'virtual-meetings', section: 'classes-enrollment', titleId: 'classes.webex.adminHomeCategory', icon: Video }],
    links: [
      { id: 'create-class', category: 'classes', labelId: 'classes.classForm.create.title', path: createClassPath, permission: writeClassesPermission, permissionScope: organizationScopes },
      { id: 'find-classes', category: 'classes', labelId: 'classes.find.classes.title', path: findClassesPath, permission: readClassesPermission, permissionScope: organizationScopes },
      { id: 'find-sessions', category: 'classes', labelId: 'classes.find.sessions.title', path: findSessionsPath, permission: readClassesPermission, permissionScope: organizationScopes },
      { id: 'class-schedule', category: 'classes', labelId: 'classes.find.schedule.title', path: classSchedulePath, permission: readClassesPermission, permissionScope: organizationScopes },
      { id: 'instructor-schedule', category: 'classes', labelId: 'classes.calendars.schedule.title', path: instructorSchedulePath, permission: readClassesPermission, permissionScope: organizationScopes },
      { id: 'find-class-attendance', category: 'classes', labelId: 'classes.find.attendance.title', path: findAttendancePath, permission: readClassRosterPermission, permissionScope: organizationScopes },
      { id: 'find-meeting-joins', category: 'virtual-meetings', labelId: 'classes.find.meetingJoins.title', path: findMeetingJoinsPath, permission: readClassRosterPermission, permissionScope: organizationScopes },
      {
        id: 'find-webex-connections',
        category: 'virtual-meetings',
        labelId: 'classes.webex.find.title',
        path: webExConnectionsPath,
        permission: readWebExConnectionsPermission,
      },
      // Class Custom Fields, under Classes as Course Custom Fields sit under Courses (CLS-19).
      {
        id: 'find-class-custom-fields',
        category: 'classes',
        labelId: 'classes.customFields.find.title',
        path: classCustomFieldsPath,
        permission: readClassCustomFieldsPermission,
      },
      {
        id: 'create-class-custom-field',
        category: 'classes',
        labelId: 'classes.customFields.create.title',
        path: createClassCustomFieldPath,
        permission: writeClassCustomFieldsPermission,
      },
    ],
  },
  slots: [
    ...classFormSections,
    ...classDetailsTabs,
    ...classDetailsSidebar,
    {
      // The class's custom fields on Class Details (CLS-19).
      kind: 'panel',
      slot: detailsSlots(classDetailsSlot).panels,
      id: 'custom-fields',
      component: () => import('./custom-fields/ClassCustomFieldsPanel').then((module) => module.ClassCustomFieldsPanel),
    },
    {
      // A course's upcoming classes on Catalog's course page, composed here since Catalog cannot call Classes (CLS-05).
      kind: 'panel',
      slot: courseOverviewSlot,
      id: 'upcoming-classes',
      component: () => import('./learner/UpcomingClassesPanel').then((module) => module.UpcomingClassesPanel),
    },
    {
      // And on the course's learner page, signed out too, as the site's anonymous class browsing allows (CAT-29).
      kind: 'panel',
      slot: courseCatalogSlots.overview,
      id: 'upcoming-classes',
      component: () => import('./learner/UpcomingClassesPanel').then((module) => module.LearnerUpcomingClassesPanel),
    },
    {
      // The class's files for its Enrolled students and its instructors on the learner class page (CLS-17).
      kind: 'panel',
      slot: detailsSlots(classSlots.learnerPage).panels,
      id: 'class-files',
      component: () => import('./files/LearnerClassFilesPanel').then((module) => module.LearnerClassFilesPanel),
    },
    {
      // The learner's week at a glance and month calendar on Entitlements' Student Dashboard (CLS-30).
      kind: 'panel',
      slot: studentDashboardSlots.main,
      id: 'week-at-a-glance',
      component: () => import('./dashboard/StudentCalendarPanels').then((module) => module.StudentWeekPanel),
    },
    {
      kind: 'panel',
      slot: studentDashboardSlots.sideTop,
      id: 'month-calendar',
      component: () => import('./dashboard/StudentCalendarPanels').then((module) => module.StudentMonthPanel),
    },
    {
      // The enrollment's completion status, grade and hours on Entitlements' Enrollment Details (CLS-11).
      kind: 'panel',
      slot: enrollmentClassPanelSlot,
      id: 'enrollment-result',
      component: () => import('./enrollment-panel/EnrollmentClassPanel').then((module) => module.EnrollmentClassPanel),
    },
    {
      // The organization's classrooms, with "Create Classroom", on Organization Details (CLS-02).
      kind: 'tab',
      slot: detailsSlots(organizationDetailsSlot).tabs,
      id: 'classrooms',
      labelId: 'classes.classrooms.organizationTab.tab',
      component: () => import('./classrooms/OrganizationClassroomsTab').then((module) => module.OrganizationClassroomsTab),
    },
    {
      // The organization's daily student check-in page, from Organization Details' side menu (CLS-12).
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'student-check-in',
      labelId: 'classes.attendance.checkIn.title',
      icon: UserCheck,
      path: (context) => studentCheckInPath(context.recordId ?? ''),
    },
    {
      // The students whose materials the organization supplies, from Organization Details' side menu (CLS-13).
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'materials-responsibilities',
      labelId: 'classes.checklist.responsibilities.title',
      icon: Package,
      permission: readClassRosterPermission,
      path: (context) => materialsResponsibilitiesPath(context.recordId ?? ''),
    },
    {
      // The `classes` settings group on Create and Edit Organization (CLS-31).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'classes',
      labelId: 'classes.settings.label',
      component: () => import('./settings/ClassesSettingsSection').then((module) => module.ClassesSettingsSection),
    },
    {
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'preferred-instructors',
      labelId: 'classes.settings.preferredInstructors.label',
      component: () => import('./settings/PreferredInstructorsSection').then((module) => module.PreferredInstructorsSection),
    },
    {
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'preferred-partners',
      labelId: 'classes.settings.preferredPartners.label',
      component: () => import('./settings/PreferredPartnersSection').then((module) => module.PreferredPartnersSection),
    },
    {
      // The Preferred Partners and Preferred By tabs on Organization Details (CLS-31).
      kind: 'tab',
      slot: detailsSlots(organizationDetailsSlot).tabs,
      id: 'preferred-partners',
      labelId: 'classes.settings.partnersTab.tab',
      component: () => import('./settings/OrganizationPreferenceTabs').then((module) => module.PreferredPartnersTab),
    },
    {
      kind: 'tab',
      slot: detailsSlots(organizationDetailsSlot).tabs,
      id: 'preferred-by',
      labelId: 'classes.settings.preferredByTab.tab',
      component: () => import('./settings/OrganizationPreferenceTabs').then((module) => module.PreferredByTab),
    },
    {
      // The organization's calendar, room calendar and instructor calendar, from Organization Details' side menu (CLS-24).
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'calendar',
      labelId: 'classes.calendars.organization.title',
      icon: CalendarDays,
      permission: readClassesPermission,
      path: (context) => organizationCalendarPath(context.recordId ?? ''),
    },
    {
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'room-calendar',
      labelId: 'classes.calendars.room.title',
      icon: DoorOpen,
      permission: readClassesPermission,
      path: (context) => roomCalendarPath(context.recordId ?? ''),
    },
    {
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'instructor-calendar',
      labelId: 'classes.calendars.instructor.title',
      icon: GraduationCap,
      permission: readClassesPermission,
      path: (context) => instructorCalendarPath(context.recordId ?? ''),
    },
    // Classes' part of Download My Data on My Profile (CLS-32).
    { kind: 'link', slot: myDataSlot, id: 'classes.my-data', labelId: 'classes.myData.part', path: myDataApi },
  ],
}
